import { isNil, range } from 'ramda';
import { isNotNil } from 'ramda-adjunct';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStack } from '../utils/useStack';
import { useOnEscapeKeyPress, useIsShiftPressed } from './useKeyPresses';
export const useSelection = (rows, rowsCount, dependencies) => {
    const [selection, setSelection] = useState(new Set());
    const lastSelectedIndicesStack = useStack();
    useEffect(() => {
        // Clear selection when filter changes (sort is OK)
        setSelection(new Set());
        lastSelectedIndicesStack.clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);
    const deselectAll = useCallback(() => {
        setSelection(new Set());
        lastSelectedIndicesStack.clear();
    }, [lastSelectedIndicesStack]);
    const selectAll = useCallback(() => {
        setSelection(new Set(rows.map((row) => row.ipsosId).filter(isNotNil)));
        lastSelectedIndicesStack.clear();
    }, [lastSelectedIndicesStack, rows]);
    const setRangeSelected = useCallback((indexFrom, indexTo) => {
        const [from, to] = indexFrom < indexTo ? [indexFrom, indexTo] : [indexTo, indexFrom];
        const indices = range(from, to + 1);
        const rks = indices.map((index) => rows[index].ipsosId).filter(isNotNil);
        setSelection((prevSelection) => new Set([...rks, ...prevSelection]));
    }, [rows]);
    const setSelected = useCallback((row, selected) => {
        setSelection((prevSelection) => {
            const newSelection = new Set(prevSelection);
            if (selected) {
                lastSelectedIndicesStack.push(row.index);
                newSelection.add(row.ipsosId);
            }
            else {
                lastSelectedIndicesStack.without(row.index);
                newSelection.delete(row.ipsosId);
            }
            return newSelection;
        });
    }, [lastSelectedIndicesStack]);
    const selectionCount = selection.size;
    const allSelected = rowsCount > 0 && selectionCount === rowsCount;
    const isSelected = useCallback((row) => selection.has(row.ipsosId), [selection]);
    const shiftPressed = useIsShiftPressed();
    const toggleSelected = useCallback((row) => {
        const isRowSelected = isSelected(row);
        if (!shiftPressed) {
            return setSelected(row, !isRowSelected);
        }
        const lastSelectedIndex = lastSelectedIndicesStack.head;
        if (isNil(lastSelectedIndex)) {
            return;
        }
        setRangeSelected(row.index, lastSelectedIndex);
    }, [isSelected, lastSelectedIndicesStack.head, setRangeSelected, setSelected, shiftPressed]);
    useOnEscapeKeyPress(deselectAll);
    return useMemo(() => ({
        selectAll,
        deselectAll,
        setSelected,
        isSelected,
        allSelected,
        selectionCount,
        toggleSelected,
    }), [allSelected, deselectAll, isSelected, selectAll, selectionCount, setSelected, toggleSelected]);
};
//# sourceMappingURL=useSelection.js.map